/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable import/no-extraneous-dependencies */
import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import PageWrapper from "../../../layout/PageWrapper/PageWrapper";
import showNotification from "../../../components/extras/showNotification";
import Icon from "../../../components/icon/Icon";
import "../../../styles/login/phoenix-login.scss";

import { loginPages } from "../../../menu";
import {
  getLoginObjDetails,
  selectCurrentDecodedUser,
  selectCurrentUser,
  getTotpPending,
} from "../../../redux/login/login.selector";

import { validateLogin } from "../../helper/Login/loginValidates";

import {
  userSignInStart,
  userSignInResponseResetStart,
  userSignInSuccess,
  userTotpReset,
} from "../../../redux/login/login.action";
import { verifyTotpLogin } from "../../../redux/login/login.services";
import { encryptData } from "../../../redux/_common/enode-decode";

/** Phoenix glyph path from the design handoff — shared by every layer. */
const PHOENIX_PATH =
  "M0 -16 C -10 -8, -16 0, -14 8 L -22 4 C -22 18, -14 26, -4 28 L -10 30 C 0 32, 12 30, 18 22 C 28 14, 30 0, 26 -10 L 18 -4 C 18 -14, 12 -22, 0 -16 Z";

const PHOENIX_VIEWBOX = "-50 -40 100 90";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Login = (isSignUp: any) => {
  const dispatch = useDispatch();
  const [isSpinner, setSpinner] = useState(false);
  const [isShake, setShake] = useState(false);
  const [isLeaving, setLeaving] = useState(false);
  const navigate = useNavigate();
  const loginData = useSelector(getLoginObjDetails);

  const getCurrentUserLoginDetails = useSelector(selectCurrentUser);
  const loginRespsArr = useSelector(selectCurrentDecodedUser);

  // ---- 2FA (Google Authenticator) login step-2 ----
  const totpPending = useSelector(getTotpPending);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");

  useEffect(() => {
    // stop the password spinner once 2FA is required, so the code form shows
    if (totpPending) setSpinner(false);
  }, [totpPending]);

  const handlePinVerify = async () => {
    if (!totpPending || pin.length < 6) {
      setPinErr("Enter the 6-digit code from your authenticator app");
      return;
    }
    setSpinner(true);
    setPinErr("");
    try {
      const token = encryptData(
        { PendingToken: totpPending.PendingToken, Code: pin } as any,
        "VerifyLoginTotpKey",
      );
      const resp: any = await verifyTotpLogin(token as any);
      if (resp?.data?.statusCode === "01") {
        dispatch(userSignInSuccess(resp.data)); // success effect navigates
      } else if (resp?.data?.statusCode === "440") {
        setPinErr("Your session expired — please sign in again.");
        dispatch(userTotpReset());
        setPin("");
        setSpinner(false);
      } else {
        setPinErr("Invalid code. Please try again.");
        setSpinner(false);
      }
    } catch (e) {
      setPinErr("Could not verify the code. Please try again.");
      setSpinner(false);
    }
  };

  const stageRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLFormElement>(null);
  const embersRef = useRef<HTMLDivElement>(null);

  // Star field — fixed positions per mount.
  const stars = useMemo(
    () =>
      Array.from({ length: 120 }, () => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        size: 0.5 + Math.random() * 2.4,
        delay: `${Math.random() * 4}s`,
        opacity: 0.2 + Math.random() * 0.7,
      })),
    [],
  );

  // Mouse parallax — smoothed targets drive CSS vars on the stage element.
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const stage = stageRef.current;
    const card = cardRef.current;
    if (!stage) return undefined;

    let targetMx = 0;
    let targetMy = 0;
    let targetTx = 0;
    let targetTy = 0;
    let mx = 0;
    let my = 0;
    let tx = 0;
    let ty = 0;
    let raf = 0;

    const tick = () => {
      mx += (targetMx - mx) * 0.08;
      my += (targetMy - my) * 0.08;
      tx += (targetTx - tx) * 0.1;
      ty += (targetTy - ty) * 0.1;
      stage.style.setProperty("--mx", mx.toFixed(3));
      stage.style.setProperty("--my", my.toFixed(3));
      stage.style.setProperty("--tx", tx.toFixed(3));
      stage.style.setProperty("--ty", ty.toFixed(3));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onMove = (e: MouseEvent) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      targetMx = (e.clientX - cx) / cx;
      targetMy = (e.clientY - cy) / cy;
    };
    const onCardMove = (e: MouseEvent) => {
      if (!card) return;
      const r = card.getBoundingClientRect();
      targetTx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      targetTy = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    const onCardLeave = () => {
      targetTx = 0;
      targetTy = 0;
    };

    window.addEventListener("mousemove", onMove);
    card?.addEventListener("mousemove", onCardMove);
    card?.addEventListener("mouseleave", onCardLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      card?.removeEventListener("mousemove", onCardMove);
      card?.removeEventListener("mouseleave", onCardLeave);
    };
  }, []);

  // Ember particles drifting up from the bottom.
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const host = embersRef.current;
    if (!host) return undefined;

    const makeEmber = () => {
      const ember = document.createElement("span");
      ember.className = "phx-ember";
      const size = 3 + Math.random() * 6;
      ember.style.width = `${size}px`;
      ember.style.height = `${size}px`;
      ember.style.left = `${10 + Math.random() * 80}%`;
      ember.style.bottom = "-20px";
      const dur = 6 + Math.random() * 8;
      ember.style.animationDuration = `${dur}s`;
      ember.style.setProperty("--drift", `${Math.random() * 80 - 40}px`);
      host.appendChild(ember);
      window.setTimeout(() => ember.remove(), dur * 1000 + 200);
    };

    const seeds = Array.from({ length: 12 }, (_, i) =>
      window.setTimeout(makeEmber, i * 600),
    );
    const interval = window.setInterval(makeEmber, 700);
    return () => {
      seeds.forEach(window.clearTimeout);
      window.clearInterval(interval);
      host.innerHTML = "";
    };
  }, []);

  useEffect(() => {
    if (loginRespsArr && loginRespsArr.length > 0 && !isLeaving) {
      leaveToDashboard();
    }
  }, [loginRespsArr, navigate]);

  useEffect(() => {
    if (getCurrentUserLoginDetails != null) {
      const statusCode =
        getCurrentUserLoginDetails?.statusCode ||
        getCurrentUserLoginDetails?.StatusCode;
      const hasUserData = Array.isArray(loginRespsArr) && loginRespsArr.length > 0;
      const isSuccess = statusCode === "01" || hasUserData;
      if (isSuccess) {
        leaveToDashboard();
      } else {
        setSpinner(false);
        setShake(true);
        window.setTimeout(() => setShake(false), 450);
        showNotification(
          <span className="d-flex align-items-center">
            <Icon icon="primary" size="lg" className="me-1" />
            <span>Error</span>
          </span>,
          "Please, Check your login credentials",
          "danger",
        );
        dispatch(userSignInResponseResetStart());
      }
    }
  }, [getCurrentUserLoginDetails, loginRespsArr]);

  /** Blur-out the stage, then enter the app with the arrival animation. */
  const leaveToDashboard = () => {
    if (isLeaving) return;
    setLeaving(true);
    try {
      sessionStorage.setItem("phoenix_just_logged_in", "1");
    } catch (_) {
      /* private mode */
    }
    dispatch(userSignInResponseResetStart());
    if (prefersReducedMotion()) {
      navigate(loginPages.dashboard.path);
      return;
    }
    stageRef.current?.classList.add("is-out");
    window.setTimeout(() => navigate(loginPages.dashboard.path), 520);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: "",
      password: "",
      isView: false,
    },
    validate: validateLogin,
    validateOnChange: false,
    onSubmit: (values) => {
      setSpinner(true);
      dispatch(userSignInStart(values));
    },
  });

  const phoenixSvg = (defs: React.ReactNode, fill: string, extra?: React.ReactNode) => (
    <svg viewBox={PHOENIX_VIEWBOX} xmlns="http://www.w3.org/2000/svg">
      {defs}
      <path d={PHOENIX_PATH} fill={fill} />
      {extra}
    </svg>
  );

  return (
    <PageWrapper className="phx-login-page" title="Sign in">
      <div className={`phx-stage${isLeaving ? " is-out" : ""}`} ref={stageRef}>
        {/* depth 1 — stars */}
        <div className="phx-layer phx-stars" aria-hidden="true">
          {stars.map((s, i) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="phx-stars__dot"
              style={{
                left: s.left,
                top: s.top,
                width: s.size,
                height: s.size,
                animationDelay: s.delay,
                opacity: s.opacity,
              }}
            />
          ))}
        </div>

        {/* depth — orbital ring */}
        <div className="phx-layer phx-ring" aria-hidden="true">
          <div className="phx-ring__el" />
        </div>

        {/* depth — aura blobs */}
        <div className="phx-layer phx-aura" aria-hidden="true">
          <div className="phx-aura__blob" />
          <div className="phx-aura__blob phx-aura__blob--2" />
        </div>

        {/* depth — phoenix shadow */}
        <div className="phx-layer phx-phoenix-shadow" aria-hidden="true">
          {phoenixSvg(
            <defs>
              <radialGradient id="phxShadowGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="oklch(0.55 0.26 295)" stopOpacity="0.9" />
                <stop offset="100%" stopColor="oklch(0.30 0.20 320)" stopOpacity="0" />
              </radialGradient>
            </defs>,
            "url(#phxShadowGrad)",
          )}
        </div>

        {/* depth — phoenix glow */}
        <div className="phx-layer phx-phoenix-glow" aria-hidden="true">
          {phoenixSvg(
            <defs>
              <linearGradient id="phxGlowGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="oklch(0.85 0.20 295)" />
                <stop offset="50%" stopColor="oklch(0.62 0.24 295)" />
                <stop offset="100%" stopColor="oklch(0.42 0.22 320)" />
              </linearGradient>
            </defs>,
            "url(#phxGlowGrad)",
          )}
        </div>

        {/* depth — phoenix body */}
        <div className="phx-layer phx-phoenix-body" aria-hidden="true">
          <svg viewBox={PHOENIX_VIEWBOX} xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="phxBodyGrad" x1="0" y1="0" x2="0.6" y2="1">
                <stop offset="0%" stopColor="oklch(0.78 0.18 300)" />
                <stop offset="55%" stopColor="oklch(0.55 0.24 295)" />
                <stop offset="100%" stopColor="oklch(0.32 0.18 305)" />
              </linearGradient>
              <linearGradient id="phxBodyHi" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="oklch(0.96 0.08 295)" stopOpacity="0.55" />
                <stop offset="100%" stopColor="oklch(0.78 0.18 300)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={PHOENIX_PATH} fill="url(#phxBodyGrad)" />
            <path d={PHOENIX_PATH} fill="url(#phxBodyHi)" />
            <circle cx="-6" cy="-6" r="1.7" fill="oklch(0.98 0.04 290)" />
            <circle cx="-6" cy="-6" r="0.7" fill="oklch(0.20 0.06 290)" />
          </svg>
        </div>

        {/* depth — phoenix outline highlight */}
        <div className="phx-layer phx-phoenix-edge" aria-hidden="true">
          <svg viewBox={PHOENIX_VIEWBOX} xmlns="http://www.w3.org/2000/svg">
            <path
              d={PHOENIX_PATH}
              fill="none"
              stroke="oklch(0.95 0.10 295 / 0.7)"
              strokeWidth="0.4"
            />
          </svg>
        </div>

        {/* depth — embers */}
        <div className="phx-layer phx-embers" ref={embersRef} aria-hidden="true" />

        {/* ======== foreground ======== */}
        <div className="phx-ui">
          <section className="phx-brand-side">
            <div className="phx-brand-mark">
              <div className="phx-brand-mark__logo">
                <svg width="22" height="22" viewBox={PHOENIX_VIEWBOX} fill="white">
                  <path d={PHOENIX_PATH} opacity="0.95" />
                </svg>
              </div>
              <div className="phx-brand-mark__name">
                PHOENIX <span>· CRM</span>
              </div>
            </div>

            <div className="phx-brand-hero">
              <div className="phx-brand-hero__eyebrow">Commercial Finance Cloud</div>
              <h1>
                Rise from <em>every</em> deal.
              </h1>
              <p>
                The lending ops cockpit your team actually wants to live in —
                proposals, pipeline, and people, all in one calmer place.
              </p>
            </div>

            <div className="phx-brand-foot">
              <div className="phx-brand-foot__stat">
                <strong>Proposals</strong>
                End-to-end tracking
              </div>
              <div className="phx-brand-foot__sep" />
              <div className="phx-brand-foot__stat">
                <strong>Pipeline</strong>
                Live lender status
              </div>
              <div className="phx-brand-foot__sep" />
              <div className="phx-brand-foot__stat">
                <strong>Reports</strong>
                RPC &amp; performance
              </div>
            </div>
          </section>

          <section className="phx-form-side">
            <form
              className={`phx-card${isShake ? " is-error" : ""}`}
              ref={cardRef}
              onSubmit={
                totpPending
                  ? (e) => {
                      e.preventDefault();
                      handlePinVerify();
                    }
                  : formik.handleSubmit
              }
              autoComplete="on"
              noValidate
            >
              <div className="phx-card__head">
                <div className="phx-card__eyebrow">Sign in to your workspace</div>
                <h2 className="phx-card__title">Welcome back.</h2>
                <p className="phx-card__sub">
                  Continue where you left off — we kept your seat warm.
                </p>
              </div>

              {totpPending && (
                <div className="phx-field">
                  <div className="phx-field__row">
                    <label className="phx-field__label" htmlFor="totpPin">
                      Two-factor code
                    </label>
                  </div>
                  <p
                    className="phx-card__sub"
                    style={{ marginTop: 0, marginBottom: 10 }}
                  >
                    {totpPending.TotpEnabled && totpPending.PinSet
                      ? "Enter your 6-digit login PIN or the code from your Google Authenticator app."
                      : totpPending.PinSet
                        ? "Enter your 6-digit login PIN."
                        : "Enter the 6-digit code from your Google Authenticator app."}
                  </p>
                  <div className="phx-field__input">
                    <input
                      id="totpPin"
                      name="totpPin"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      autoComplete="one-time-code"
                      autoFocus
                      placeholder="000000"
                      style={{ letterSpacing: "0.4em", fontWeight: 600 }}
                      value={pin}
                      onChange={(e) => {
                        setPin(e.target.value.replace(/\D/g, "").slice(0, 6));
                        setPinErr("");
                      }}
                    />
                  </div>
                  {pinErr && (
                    <div
                      style={{ color: "#ff8080", fontSize: 13, marginTop: 8 }}
                    >
                      {pinErr}
                    </div>
                  )}
                  <button
                    type="button"
                    style={{
                      marginTop: 10,
                      background: "none",
                      border: "none",
                      color: "#a78bfa",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 13,
                    }}
                    onClick={() => {
                      dispatch(userTotpReset());
                      setPin("");
                      setPinErr("");
                      setSpinner(false);
                    }}
                  >
                    ← Back to sign in
                  </button>
                </div>
              )}

              {!totpPending && (
                <>
              <div className="phx-field">
                <div className="phx-field__row">
                  <label className="phx-field__label" htmlFor="phoneNumber">
                    Enter Mobile Number
                  </label>
                </div>
                <div className="phx-field__input">
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="text"
                    placeholder="Enter Phone Number"
                    autoComplete="username"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="6" y="2" width="12" height="20" rx="2" />
                    <path d="M11 18h2" />
                  </svg>
                </div>
              </div>

              <div className="phx-field">
                <div className="phx-field__row">
                  <label className="phx-field__label" htmlFor="password">
                    Password
                  </label>
                </div>
                <div className="phx-field__input">
                  <input
                    id="password"
                    name="password"
                    type={formik.values.isView ? "text" : "password"}
                    placeholder="Enter Password"
                    autoComplete="current-password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 1 1 8 0v4" />
                  </svg>
                  <button
                    type="button"
                    className="phx-field__reveal"
                    aria-label={formik.values.isView ? "Hide password" : "Show password"}
                    onClick={() => formik.setFieldValue("isView", !formik.values.isView)}
                  >
                    {formik.values.isView ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m3 3 18 18" />
                        <path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.2M6.6 6.6C3.8 8.5 2 12 2 12s3.5 7 10 7c1.4 0 2.7-.3 3.8-.8" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="phx-check-row">
                <label className="phx-checkbox">
                  <input type="checkbox" defaultChecked />
                  <span className="phx-checkbox__box" />
                  Keep me signed in
                </label>
                <span className="phx-sso-tag">Secure · AES</span>
              </div>
                </>
              )}

              <button
                type="submit"
                className="phx-btn-primary"
                disabled={isSpinner}
                aria-label={totpPending ? "Verify code" : "Sign In"}
              >
                <span>
                  {isSpinner
                    ? totpPending
                      ? "Verifying…"
                      : "Signing in…"
                    : totpPending
                      ? "Verify code"
                      : "Enter Phoenix"}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="m13 6 6 6-6 6" />
                </svg>
              </button>
            </form>
          </section>
        </div>

        <div className="phx-meta-corner">
          <span className="phx-meta-corner__dot" />
          All systems operational · eu-west-2
        </div>
      </div>
    </PageWrapper>
  );
};

Login.propTypes = {
  isSignUp: PropTypes.bool,
};
export default Login;
